// The stats page (components/stats/StatsSheet): the household's shopping read
// like a training log. Everything comes from rows the app already keeps — the
// item history (bought rows grouped by the Finish trip that cleared them, as
// in lib/trips.ts) and, from shopping mode on, the trips table (037) for
// stores and what a trip cost. Pure and tested; the sheet only draws it.

import type { Item, Trip } from './supabase'
import { itemKey } from './normalize'
import { deletedFinishes, pastTrips } from './trips'

const DAY = 86_400_000
/** "This month" is the last 30 days, so the numbers never reset to zero on the 1st. */
const WINDOW_DAYS = 30
const WEEKS = 8
const MONTHS = 6
const STAPLE_DAYS = 90
const STAPLES = 6
/** A staple needs this many purchases before it gets an "every n days". */
const CADENCE_MIN = 3
/** A usual shopping day needs this many trips in the weeks shown. */
const USUAL_DAY_MIN = 3

export type HistoryRow = Pick<Item, 'name' | 'category_id' | 'done' | 'done_at' | 'deleted_at' | 'created_by' | 'created_at'>

export interface Stats {
  window: { trips: number; items: number; avgMs: number | null; spend: Money | null }
  weeks: { start: Date; trips: number }[]
  /** 0 = Sunday … 6 = Saturday (Date#getDay), when there is a clear habit. */
  usualDay: number | null
  spendByMonth: { month: Date; amount: number }[]
  currency: string | null
  staples: { name: string; times: number; everyDays: number | null }[]
  categories: { categoryId: string | null; items: number; share: number }[]
  stores: { storeId: string; trips: number; avgMs: number | null; avgSpend: number | null }[]
  adders: { memberId: string | null; items: number }[]
  /** Nothing bought yet: the page shows how it fills in instead. */
  empty: boolean
}

export interface Money { amount: number; currency: string }

const median = (xs: number[]) => {
  const s = [...xs].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2
}

/** Monday 00:00 of the week `d` is in, local time. */
export function weekStart(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  out.setDate(out.getDate() - ((out.getDay() + 6) % 7))
  return out
}

const monthStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1)

/** The currency most of the costed trips were in; amounts in others are left out of sums. */
function mainCurrency(trips: Pick<Trip, 'cost' | 'currency'>[]): string | null {
  const counts = new Map<string, number>()
  for (const t of trips) if (t.cost !== null && t.currency) counts.set(t.currency, (counts.get(t.currency) ?? 0) + 1)
  return [...counts].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null
}

export function buildStats(history: HistoryRow[], live: Pick<Item, 'created_by' | 'created_at'>[], trips: Trip[], now: Date): Stats {
  const since = now.getTime() - WINDOW_DAYS * DAY
  const deleted = deletedFinishes(trips)
  const bought = history.filter(r => r.done && r.done_at && r.deleted_at && !deleted.has(Date.parse(r.deleted_at)))
  const all = pastTrips(history, deleted)
  const recent = all.filter(t => t.finishedAt >= since)
  const timed = recent.flatMap(t => (t.ms === null ? [] : [t.ms]))
  const finished = trips.filter(t => t.finished_at && !t.deleted_at)
  const currency = mainCurrency(finished)
  const costed = finished.filter(t => t.cost !== null && t.currency === currency)
  const recentSpend = costed.filter(t => Date.parse(t.finished_at!) >= since)

  // Trips per week, oldest first, this week last.
  const thisWeek = weekStart(now)
  const weeks = Array.from({ length: WEEKS }, (_, i) => {
    const start = new Date(thisWeek)
    start.setDate(start.getDate() - (WEEKS - 1 - i) * 7)
    return { start, trips: 0 }
  })
  const dayCounts = new Array(7).fill(0)
  for (const t of all) {
    const at = new Date(t.finishedAt)
    const w = weeks.findIndex((wk, i) => at >= wk.start && (i === WEEKS - 1 || at < weeks[i + 1].start))
    if (w >= 0) {
      weeks[w].trips += 1
      dayCounts[at.getDay()] += 1
    }
  }
  const inWeeks = weeks.reduce((n, w) => n + w.trips, 0)
  const topDay = dayCounts.indexOf(Math.max(...dayCounts))
  const usualDay = inWeeks >= USUAL_DAY_MIN && dayCounts[topDay] / inWeeks >= 0.4 ? topDay : null

  // Spend per calendar month, this month last.
  const thisMonth = monthStart(now)
  const spendByMonth = currency
    ? Array.from({ length: MONTHS }, (_, i) => {
      const month = new Date(thisMonth.getFullYear(), thisMonth.getMonth() - (MONTHS - 1 - i), 1)
      const next = new Date(month.getFullYear(), month.getMonth() + 1, 1)
      const amount = costed
        .filter(t => { const at = Date.parse(t.finished_at!); return at >= month.getTime() && at < next.getTime() })
        .reduce((sum, t) => sum + (t.cost ?? 0), 0)
      return { month, amount: Math.round(amount * 100) / 100 }
    })
    : []

  // Staples: what was bought most in the last 90 days, by item (word order and synonyms aside).
  const stapleSince = now.getTime() - STAPLE_DAYS * DAY
  const byKey = new Map<string, { name: string; latest: number; days: Set<number> }>()
  for (const r of bought) {
    const at = Date.parse(r.done_at!)
    if (at < stapleSince) continue
    const key = itemKey(r.name)
    if (!key) continue
    const entry = byKey.get(key) ?? { name: r.name, latest: 0, days: new Set<number>() }
    if (at > entry.latest) { entry.latest = at; entry.name = r.name }
    entry.days.add(Math.floor(at / DAY))
    byKey.set(key, entry)
  }
  const staples = [...byKey.values()]
    .map(e => {
      const days = [...e.days].sort((a, b) => a - b)
      const gaps = days.slice(1).map((d, i) => d - days[i])
      return { name: e.name, times: days.length, everyDays: days.length >= CADENCE_MIN ? Math.round(median(gaps) * 10) / 10 : null }
    })
    .sort((a, b) => b.times - a.times || a.name.localeCompare(b.name))
    .slice(0, STAPLES)

  // Sections: what share of the last 30 days' items each category had.
  const recentBought = bought.filter(r => Date.parse(r.done_at!) >= since)
  const catCounts = new Map<string | null, number>()
  for (const r of recentBought) catCounts.set(r.category_id, (catCounts.get(r.category_id) ?? 0) + 1)
  const categories = [...catCounts]
    .map(([categoryId, items]) => ({ categoryId, items, share: items / recentBought.length }))
    .sort((a, b) => b.items - a.items)

  // Stores, from the trips shopping mode recorded.
  const byStore = new Map<string, Trip[]>()
  for (const t of finished) if (t.store_id) byStore.set(t.store_id, [...(byStore.get(t.store_id) ?? []), t])
  const stores = [...byStore].map(([storeId, ts]) => {
    const ms = ts.map(t => Date.parse(t.finished_at!) - Date.parse(t.started_at)).filter(m => m >= 60_000)
    const spends = ts.filter(t => t.cost !== null && t.currency === currency).map(t => t.cost!)
    return {
      storeId,
      trips: ts.length,
      avgMs: ms.length ? median(ms) : null,
      avgSpend: spends.length ? Math.round((spends.reduce((a, b) => a + b, 0) / spends.length) * 100) / 100 : null,
    }
  }).sort((a, b) => b.trips - a.trips)

  // Who put things on the list in the last 30 days, bought or not yet.
  const addCounts = new Map<string | null, number>()
  for (const r of [...history, ...live]) {
    if (!r.created_at || Date.parse(r.created_at) < since) continue
    addCounts.set(r.created_by, (addCounts.get(r.created_by) ?? 0) + 1)
  }
  const adders = [...addCounts].map(([memberId, items]) => ({ memberId, items })).sort((a, b) => b.items - a.items)

  return {
    window: {
      trips: recent.length,
      items: recentBought.length,
      avgMs: timed.length ? median(timed) : null,
      spend: currency && recentSpend.length
        ? { amount: Math.round(recentSpend.reduce((s, t) => s + (t.cost ?? 0), 0) * 100) / 100, currency }
        : null,
    },
    weeks,
    usualDay,
    spendByMonth,
    currency,
    staples,
    categories,
    stores,
    adders,
    empty: bought.length === 0,
  }
}

/** "every 4 days", "every week", "every 2 weeks" — a staple's cadence in words. */
export function cadence(days: number): string {
  if (days === 1) return 'every day'
  if (days % 7 === 0) return days === 7 ? 'every week' : `every ${days / 7} weeks`
  return `every ${days} days`
}
